/**
 * @file useSeedReset.js
 * @description 초기화 미리보기, 확인, 실행과 완료 모달 상태를 관리합니다.
 * @used-in pages/admin/HistoryAdminPanel.jsx, pages/admin/FaqAdminPanel.jsx, pages/admin/NewsAdminPanel.jsx, pages/admin/ContentAdminPanel.jsx
 */
import { useEffect, useRef, useState } from 'react';
import { AdminAuthError, fetchResetPreview, resetToSeed } from '../services/adminApi';
import { RESET_TARGET_LABELS, buildResetConfirmMessage, buildResetDoneMessage } from '../services/resetMessage';

// [기능] 패널별 초기화 요청과 공용 모달에 전달할 속성을 제공합니다.
export function useSeedReset({ target, isDirty, onApplied, onError, onUnauthorized }) {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const statusRef = useRef('idle');
  const isMountedRef = useRef(false);
  const latestRef = useRef({ isDirty, onApplied, onError, onUnauthorized });
  latestRef.current = { isDirty, onApplied, onError, onUnauthorized };

  // [기능] 마운트 생명주기를 추적하여 떠난 패널의 응답을 무시합니다.
  useEffect(() => {
    // [기술] StrictMode의 effect 재실행에서도 응답을 적용할 수 있도록 본문에서 복원합니다.
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // [기능] 화면 상태와 중복 요청 차단용 상태를 함께 전환합니다.
  const changeStatus = (next) => {
    statusRef.current = next;
    setStatus(next);
  };

  // [기능] 인증 만료와 일반 오류를 구분하여 패널에 전달합니다.
  const handleError = (error) => {
    if (!isMountedRef.current) return;
    changeStatus('idle');
    // [비즈니스] 세션 만료는 오류 안내 대신 기존 로그인 처리로 넘깁니다.
    if (error instanceof AdminAuthError) latestRef.current.onUnauthorized();
    else latestRef.current.onError(error.message);
  };

  // [기능] 현재·초기 데이터 건수를 조회하고 확인 창을 엽니다.
  const requestReset = async () => {
    // [기술] 같은 렌더 안의 연속 클릭도 막기 위해 동기적으로 갱신하는 ref를 검사합니다.
    if (statusRef.current !== 'idle') return;
    latestRef.current.onError('');
    changeStatus('preview');
    try {
      const preview = await fetchResetPreview(target);
      if (!isMountedRef.current) return;
      setMessage(buildResetConfirmMessage(target, preview, { isDirty: latestRef.current.isDirty }));
      changeStatus('confirm');
    } catch (error) {
      handleError(error);
    }
  };

  // [기능] 확인 창을 취소하거나 완료 안내를 닫습니다.
  const cancel = () => { changeStatus('idle'); };

  // [기능] 확인한 초기화를 실행하고 응답 데이터를 패널에 적용합니다.
  const onConfirm = async () => {
    if (statusRef.current === 'done') { cancel(); return; }
    if (statusRef.current !== 'confirm') return;
    changeStatus('running');
    try {
      const result = await resetToSeed(target);
      if (!isMountedRef.current) return;
      latestRef.current.onApplied(result.data);
      setMessage(buildResetDoneMessage(target, result));
      changeStatus('done');
    } catch (error) {
      handleError(error);
    }
  };

  return {
    isResetting: status === 'preview' || status === 'running',
    requestReset,
    dialogProps: {
      isOpen: status === 'confirm' || status === 'done',
      title: `${RESET_TARGET_LABELS[target]} 초기화${status === 'done' ? ' 완료' : ''}`,
      message,
      confirmLabel: status === 'confirm' ? '초기화' : '확인',
      cancelLabel: '취소',
      onConfirm,
      onCancel: status === 'confirm' ? cancel : undefined,
    },
  };
}
